import { sleep } from '@remote-voice/utilities';

import AudioContextSingleton from './AudioContextSingleton';

const bufferSize = 3;
const maxRetryAttempts = 3;

interface PendingChunk {
  chunk: Uint8Array;
  chunkIndex: number;
  isLastChunk: boolean;
  retryCount?: number;
}

interface WAVHeader {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  byteRate: number;
}

export class AudioStream {
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;

  private audioContext: AudioContext | null = null;
  private nextStartTime = 0;
  private wavHeader: WAVHeader | null = null;
  private scheduledSources: AudioBufferSourceNode[] = [];

  private isInitialized = false;
  private isPlaying = false;
  private hasStartedPlaying = false;

  private pendingChunks: PendingChunk[] = [];
  private isProcessing = false;
  private bufferedChunks = 0;

  private onCompleteCallback: (() => void) | null = null;
  private receivedLastChunk = false;
  private detectedMimeType: string | null = null;
  private isFirstChunk = true;

  private playCounter = 0; // 前の音声処理のイベントが、次の音声処理に作用するのを防ぐため。

  async playChunk(
    chunk: Uint8Array,
    chunkIndex: number,
    isLastChunk: boolean,
    onComplete?: () => void
  ): Promise<void> {
    if (onComplete) {
      this.onCompleteCallback = onComplete;
    }

    if (!chunk || chunk.length === 0) {
      return;
    }

    if (this.isFirstChunk) {
      this.isFirstChunk = false;
      const detectedFormat = this.detectAudioFormat(chunk);
      this.detectedMimeType = detectedFormat;

      if (detectedFormat === 'audio/wav') {
        const header = this.parseWAVHeader(chunk);
        if (header) {
          this.wavHeader = header;
        } else {
          console.error('Failed to parse WAV header');
          return;
        }
      }
    }

    if (this.detectedMimeType !== 'audio/wav' && !this.isInitialized) {
      try {
        //Media source not support on ios safari
        await this.initializeMediaSource(this.detectedMimeType || 'audio/mpeg');
      } catch (error) {
        console.error('Failed to initialize web audio system:', error);
        return;
      }
    }

    //Need re-check audio state because after screen hide, isInitialized maybe stays true while audio state change
    if (
      this.detectedMimeType === 'audio/wav' &&
      (this.audioContext?.state !== 'running' || !this.isInitialized)
    ) {
      try {
        await this.initializeWebAudio();
      } catch (error) {
        console.error('Failed to initialize audio system:', error);
        return;
      }
    }

    console.debug('[TTS-stream] playChunk', {
      audioContextState: this.audioContext?.state,
      audioContextType: (navigator as any)?.audioSession?.type,
    });

    this.pendingChunks.push({
      chunk,
      chunkIndex,
      isLastChunk,
      retryCount: 0,
    });

    this.pendingChunks.sort((a, b) => a.chunkIndex - b.chunkIndex);

    if (isLastChunk) {
      this.receivedLastChunk = true;
    }

    if (!this.isProcessing) {
      this.processNextChunk();
    }
  }

  async reset(): Promise<void> {
    try {
      this.scheduledSources.forEach((source) => {
        try {
          source.stop();
          source.disconnect();
        } catch (error) {
          console.error('error reset audio stream', error);
        }
      });
      this.scheduledSources = [];

      // シングルトンのAudioContextは閉じない（他のインスタンスが使用している可能性があるため）
      this.audioContext = null;

      if (this.audioElement) {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement.onended = null;
        this.audioElement.onerror = null;
        this.audioElement = null;
      }

      if (this.mediaSource && this.mediaSource.readyState === 'open') {
        this.mediaSource.endOfStream();
      }

      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }

      this.mediaSource = null;
      this.sourceBuffer = null;
      this.pendingChunks = [];
      this.wavHeader = null;
      this.nextStartTime = 0;

      this.isInitialized = false;
      this.isPlaying = false;
      this.hasStartedPlaying = false;
      this.isProcessing = false;
      this.bufferedChunks = 0;
      this.onCompleteCallback = null;
      this.receivedLastChunk = false;
      this.detectedMimeType = null;
      this.isFirstChunk = true;
    } catch (error) {
      console.error('Error during reset:', error);
    }
  }

  private parseWAVHeader(chunk: Uint8Array): WAVHeader | null {
    if (chunk.length < 44) return null;

    try {
      const view = new DataView(
        chunk.buffer,
        chunk.byteOffset,
        chunk.byteLength
      );

      const riff = String.fromCharCode(
        view.getUint8(0),
        view.getUint8(1),
        view.getUint8(2),
        view.getUint8(3)
      );
      if (riff !== 'RIFF') return null;

      const wave = String.fromCharCode(
        view.getUint8(8),
        view.getUint8(9),
        view.getUint8(10),
        view.getUint8(11)
      );
      if (wave !== 'WAVE') return null;

      const channels = view.getUint16(22, true);
      const sampleRate = view.getUint32(24, true);
      const byteRate = view.getUint32(28, true);
      const bitsPerSample = view.getUint16(34, true);

      return {
        sampleRate,
        channels,
        bitsPerSample,
        byteRate,
      };
    } catch (error) {
      console.error('Error parsing WAV header:', error);
      return null;
    }
  }

  private detectAudioFormat(chunk: Uint8Array): string | null {
    if (chunk.length < 12) return null;

    if (
      chunk[0] === 0x52 &&
      chunk[1] === 0x49 &&
      chunk[2] === 0x46 &&
      chunk[3] === 0x46
    ) {
      return 'audio/wav';
    }

    if (
      (chunk[0] === 0xff && (chunk[1] & 0xe0) === 0xe0) ||
      (chunk[0] === 0x49 && chunk[1] === 0x44 && chunk[2] === 0x33)
    ) {
      return 'audio/mpeg';
    }

    if (
      chunk[0] === 0x4f &&
      chunk[1] === 0x67 &&
      chunk[2] === 0x67 &&
      chunk[3] === 0x53
    ) {
      return 'audio/ogg';
    }

    return null;
  }

  private async initializeWebAudio(): Promise<void> {
    try {
      const audioContextSingleton = AudioContextSingleton.getInstance();
      const audioContext = await audioContextSingleton.getAudioContext();

      this.audioContext = audioContext;
      this.nextStartTime = audioContext.currentTime;
      this.isInitialized = true;
      this.playCounter++;
    } catch (error) {
      console.error('Failed to initialize Web Audio API:', error);
      throw error;
    }
  }

  private findDataChunkStart(chunk: Uint8Array): number {
    for (let i = 12; i < chunk.length - 8; i++) {
      if (
        chunk[i] === 0x64 &&
        chunk[i + 1] === 0x61 &&
        chunk[i + 2] === 0x74 &&
        chunk[i + 3] === 0x61
      ) {
        return i;
      }
    }
    return -1;
  }

  private async createAudioBufferFromChunk(
    chunk: Uint8Array,
    header: WAVHeader,
    isFirstChunk: boolean
  ): Promise<AudioBuffer | null> {
    const audioContext = this.audioContext;
    if (!audioContext) return null;

    try {
      let audioData: Uint8Array;

      if (isFirstChunk) {
        const dataStart = this.findDataChunkStart(chunk);
        if (dataStart === -1) {
          console.error('Could not find data chunk in WAV file');
          return null;
        }
        audioData = chunk.slice(dataStart + 8);
      } else {
        audioData = chunk;
      }

      if (audioData.length === 0) return null;

      const bytesPerSample = header.bitsPerSample / 8;
      const samplesPerChannel =
        audioData.length / (header.channels * bytesPerSample);

      if (samplesPerChannel === 0) return null;

      const audioBuffer = audioContext.createBuffer(
        header.channels,
        Math.floor(samplesPerChannel),
        header.sampleRate
      );

      for (let channel = 0; channel < header.channels; channel++) {
        const channelData = audioBuffer.getChannelData(channel);

        for (let i = 0; i < channelData.length; i++) {
          let sample = 0;
          const sampleIndex = i * header.channels + channel;
          const byteIndex = sampleIndex * bytesPerSample;

          if (byteIndex + bytesPerSample <= audioData.length) {
            if (header.bitsPerSample === 16) {
              const view = new DataView(
                audioData.buffer,
                audioData.byteOffset + byteIndex,
                2
              );
              sample = view.getInt16(0, true) / 32768.0;
            } else if (header.bitsPerSample === 8) {
              sample = (audioData[byteIndex] - 128) / 128.0;
            } else if (header.bitsPerSample === 24) {
              const byte1 = audioData[byteIndex];
              const byte2 = audioData[byteIndex + 1];
              const byte3 = audioData[byteIndex + 2];
              sample = ((byte3 << 16) | (byte2 << 8) | byte1) / 8388608.0;
              if (sample > 1) sample = sample - 2;
            }
          }

          channelData[i] = Math.max(-1, Math.min(1, sample));
        }
      }

      return audioBuffer;
    } catch (error) {
      console.error('Error creating AudioBuffer:', error);
      return null;
    }
  }

  private async playWavChunkWithWebAudio(
    chunk: Uint8Array,
    chunkIndex: number,
    isLastChunk: boolean
  ): Promise<void> {
    const keepPlayCounter = this.playCounter;
    const audioContext = this.audioContext;
    const header = this.wavHeader;

    if (!audioContext || !header) return;

    try {
      const audioBuffer = await this.createAudioBufferFromChunk(
        chunk,
        header,
        chunkIndex === 0
      );

      if (!audioBuffer) return;

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      const currentTime = audioContext.currentTime;
      const startTime = Math.max(currentTime, this.nextStartTime);

      source.start(startTime);
      this.nextStartTime = startTime + audioBuffer.duration;

      this.scheduledSources.push(source);

      if (isLastChunk) {
        source.onended = async () => {
          // すでに次の分が始まっていたらスキップ
          if (this.playCounter != keepPlayCounter) {
            return;
          }

          if (this.onCompleteCallback) {
            try {
              this.onCompleteCallback();
            } catch (error) {
              console.error('Error in onComplete callback:', error);
            }
            this.onCompleteCallback = null;
          }
          await this.reset();
        };
      }

      if (!this.hasStartedPlaying) {
        this.hasStartedPlaying = true;
        this.isPlaying = true;
      }

      this.bufferedChunks++;
    } catch (error) {
      console.error('Error playing WAV chunk:', error);
    }
  }

  private initializeMediaSource(mimeType: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (mimeType === 'audio/wav') {
          resolve();
          return;
        }

        this.isInitialized = true;

        const mediaSource = new MediaSource();
        this.mediaSource = mediaSource;

        const objectUrl = URL.createObjectURL(mediaSource);
        this.objectUrl = objectUrl;

        const audioElement = new Audio();
        audioElement.src = objectUrl;
        audioElement.preload = 'auto';
        this.audioElement = audioElement;

        mediaSource.addEventListener('sourceopen', () => {
          try {
            if (!MediaSource.isTypeSupported(mimeType)) {
              reject(new Error(`MIME type ${mimeType} is not supported`));
              return;
            }

            const sourceBuffer = mediaSource.addSourceBuffer(mimeType);
            this.sourceBuffer = sourceBuffer;

            sourceBuffer.addEventListener('updateend', async () => {
              this.isProcessing = false;
              this.processNextChunk();
            });

            sourceBuffer.addEventListener('error', async (e) => {
              console.error('SourceBuffer error:', e);
              this.isProcessing = false;
              this.processNextChunk();
            });

            resolve();
          } catch (error) {
            reject(error);
          }
        });

        mediaSource.addEventListener('error', (e) => {
          console.error('MediaSource error:', e);
          reject(e);
        });
      } catch (error) {
        this.isInitialized = false;
        reject(error);
      }
    });
  }

  private async processNextChunk(): Promise<void> {
    if (this.isProcessing || this.pendingChunks.length === 0) return;

    if (this.detectedMimeType === 'audio/wav') {
      const nextChunk = this.pendingChunks.shift();
      if (!nextChunk) return;

      try {
        await this.playWavChunkWithWebAudio(
          nextChunk.chunk,
          nextChunk.chunkIndex,
          nextChunk.isLastChunk
        );
        if (this.pendingChunks.length > 0) {
          this.processNextChunk();
        }
      } catch (error) {
        console.error('Error processing WAV chunk:', error);
        const retryCount = (nextChunk.retryCount || 0) + 1;
        if (retryCount <= maxRetryAttempts) {
          nextChunk.retryCount = retryCount;
          this.pendingChunks.unshift(nextChunk);
          await sleep(retryCount * 100);
          this.processNextChunk();
        } else {
          console.error(
            `Failed to process WAV chunk ${nextChunk.chunkIndex} after ${maxRetryAttempts} attempts`
          );
          this.processNextChunk();
        }
      }
      return;
    }

    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer) return;

    if (sourceBuffer.updating) {
      await sleep(100);
      this.processNextChunk();
      return;
    }

    const nextChunk = this.pendingChunks.shift();
    if (!nextChunk) return;

    try {
      this.isProcessing = true;

      if (!nextChunk.chunk || nextChunk.chunk.length === 0) {
        this.isProcessing = false;
        this.processNextChunk();
        return;
      }

      sourceBuffer.appendBuffer(nextChunk.chunk as BufferSource);
      this.bufferedChunks++;
      this.checkAndStartPlayback();

      if (nextChunk.isLastChunk) {
        sourceBuffer.addEventListener(
          'updateend',
          () => {
            if (this.mediaSource && this.mediaSource.readyState === 'open') {
              this.mediaSource.endOfStream();
            }
          },
          { once: true }
        );
      }
    } catch (error) {
      console.error(`Error appending chunk ${nextChunk.chunkIndex}:`, error);
      this.isProcessing = false;

      const retryCount = (nextChunk.retryCount || 0) + 1;
      if (retryCount <= maxRetryAttempts) {
        nextChunk.retryCount = retryCount;
        this.pendingChunks.unshift(nextChunk);
        await sleep(retryCount * 100);
        this.processNextChunk();
      } else {
        console.error(
          `Failed to process chunk ${nextChunk.chunkIndex} after ${maxRetryAttempts} attempts`
        );
        this.processNextChunk();
      }
    }
  }

  private checkAndStartPlayback(): void {
    if (
      this.hasStartedPlaying ||
      !this.audioElement ||
      this.detectedMimeType === 'audio/wav'
    ) {
      return;
    }

    const shouldStartPlayback =
      this.bufferedChunks >= bufferSize ||
      (this.pendingChunks.length === 0 &&
        this.receivedLastChunk &&
        this.bufferedChunks > 0);

    if (shouldStartPlayback) {
      this.hasStartedPlaying = true;
      this.isPlaying = true;

      this.audioElement.play().catch((error) => {
        console.error('Error starting playback:', error);
        this.hasStartedPlaying = false;
        this.isPlaying = false;
      });

      this.audioElement.onended = async () => {
        if (this.onCompleteCallback) {
          try {
            this.onCompleteCallback();
          } catch (error) {
            console.error('Error in onComplete callback:', error);
          }
          this.onCompleteCallback = null;
        }
        await this.reset();
      };

      this.audioElement.onerror = async (error) => {
        console.error('Audio element error:', error);
        await this.reset();
      };
    }
  }
}
